import React, { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import ModalContext from 'contexts/ModalContext'

import { getCabinCategories, getCruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'
import useCruiseCabinDescriptions from 'hooks/Cruise/useCruiseCabinDescriptions'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Modal from 'LuxKit/Modal/Modal'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'

import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'

const PricesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(20)};

  ${mediaQueryUp.desktop} {
    display: flex;
  }
`

const PriceItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  width: ${rem(130)};
`

interface Props {
  offer: App.CruiseOffer
  departure: App.CruiseDepartureDetails
}

function CruiseDepartureTilePrices({
  offer,
  departure,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const cabinDescriptions = useCruiseCabinDescriptions()

  const openCabinInfoModal = useCallback((cabinType: App.CabinCategoryType) => {
    showModal(<Modal title={cabinTypeLabels[cabinType] ?? cabinType}>
      <BodyText variant="medium">
        {cabinDescriptions[cabinType]}
      </BodyText>
    </Modal>)
  }, [showModal, cabinTypeLabels, cabinDescriptions])

  const pricesByCabinCategory = offer.lowestDeparturePricesByCategory[departure.id]
  const isDepartureSoldOut = !pricesByCabinCategory

  const departurePrices = useMemo(() => {
    if (isDepartureSoldOut) return []

    const cabinCategoriesBasedOnCruiseType = getCabinCategories(offer.cruiseType)

    return cabinCategoriesBasedOnCruiseType.map((item) => {
      const cabinPrice = pricesByCabinCategory[item.category.toLowerCase()]

      const {
        primaryPrice,
        secondaryPrice,
      } = getCruiseOfferPriceVariation(offer, cabinPrice)

      const hasPrice = !!(primaryPrice.total || secondaryPrice?.total)

      return {
        primaryPrice,
        secondaryPrice,
        hasPrice,
        discountPercentage: cabinPrice?.brochureDiscountPercentage,
        category: item.category,
      }
    })
  }, [pricesByCabinCategory, offer, isDepartureSoldOut])

  return <PricesGrid>
    {departurePrices.map((price) => {
      return <PriceItem key={price.category}>
        {price.hasPrice && <PriceRowPriceCaption
          onInfoIconClick={() => openCabinInfoModal(price.category as App.CabinCategoryType)}
        >
          {intl.formatMessage(
            {
              id: 'b1qBznBcSC',
              defaultMessage: '{category} from',
              description: 'Cabin category price label with from prefix in cabin selection',
            },
            { category: cabinTypeLabels[price.category] ?? price.category },
          )}
        </PriceRowPriceCaption>}

        {!!price.primaryPrice.total && <PriceRowPrice
          size="S"
          price={price.primaryPrice.total}
          saleUnit={price.primaryPrice.type}
        />}

        {price.secondaryPrice && <Group direction="horizontal" gap={4}>
          <Caption variant="large" colour="neutral-three" as="span">
            <FormatCurrency value={price.secondaryPrice.total} />
          </Caption>
          <Caption variant="large" colour="neutral-three" as="span">
            {intl.formatMessage({
              id: 'oScuwLDRHT',
              defaultMessage: '/{priceType}',
              description: 'Per-unit price type label for cruise secondary price (e.g. /person)',
            }, { priceType: price.secondaryPrice.type })}
          </Caption>
        </Group>}

        {!price.hasPrice && <Group direction="vertical" gap={8}>
          <Caption variant="large" weight="normal" colour="neutral-two">
            {cabinTypeLabels[price.category] ?? price.category}
          </Caption>
          <Heading variant="heading5" colour="neutral-three">
            <FormattedMessage
              id="yMfXxKPtto"
              defaultMessage="Sold out"
              description="Label indicating cabin category is sold out in cabin selection"
            />
          </Heading>
        </Group>}

        {!!price.discountPercentage && <PriceRowValueDiscount
          type="estimate"
          discountPercentage={price.discountPercentage}
        />}

        {price.hasPrice && <Caption variant="medium" colour="neutral-two">
          <FormattedMessage
            id="5Tekcjnyh3"
            defaultMessage="Twin Share"
            description="Label indicating price is for twin share accommodation"
          />
        </Caption>}
      </PriceItem>
    })}

    {isDepartureSoldOut && <Group direction="horizontal" horizontalAlign="center">
      <BodyText variant="medium" weight="normal" colour="neutral-two">
        <FormattedMessage
          id="MS+KJ65nJR"
          defaultMessage="No prices available"
          description="Empty state message shown when no cabin prices are available for a cruise departure"
        />
      </BodyText>
    </Group>}
  </PricesGrid>
}

export default CruiseDepartureTilePrices
